import { useExpertUniversityStore } from "@expert/expert-university";
import { useSalesContext } from "@expert/sales-tools";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";
import { Stack } from "@mantine/core";
import { showNotification, hideNotification } from "@mantine/notifications";
import { useEffect } from "react";

import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useLogger } from "@/sdk/logging/LoggerProvider";

import type { BaseNotificationType } from "./models";

import { useNotificationStore } from "./stores/useNotificationStore";
import { buildLeftNotifications } from "./utils/buildLeftNotifications";
import { buildRightNotifications } from "./utils/buildRightNotifications";

export function NotificationContainer() {
    const logger = useLogger({ module: "NotificationContainer", supportTeam: "sales" });
    const notifications = useNotificationStore((state) => state.notifications);
    const addNotification = useNotificationStore((state) => state.addNotification);
    const removeNotification = useNotificationStore((state) => state.removeNotification);
    const { leaderboards } = usePartnerConfig();
    const eventBus = useExpertUniversityStore((state) => state.eventBus);
    const { isSimulatedVoiceTask } = useSalesContext();

    useEffect(() => {
        const subscription = expertWorkspaceWebSocketEventBus.on(
            "ws_notification-event",
            (payload: BaseNotificationType) => {
                logger.info(payload, "Notification received");
                addNotification(payload);
            },
        );

        return () => {
            subscription();
        };
    }, [addNotification, logger]);

    useEffect(() => {
        if (!eventBus || !isSimulatedVoiceTask) {
            hideNotification("final-behavior-reached");
            return;
        }
        const unsubscribe = eventBus.on("final-behavior-reached", () => {
            showNotification({
                id: "final-behavior-reached",
                position: "top-center",
                withCloseButton: true,
                autoClose: false,
                message: "Once you feel you have adequately demonstrated the evaluated behaviors, end the call.",
                color: "green",
            });
        });
        return () => {
            unsubscribe();
            hideNotification("final-behavior-reached");
        };
    }, [eventBus, isSimulatedVoiceTask]);

    if (leaderboards.length === 0) return undefined;

    return (
        <>
            <Stack w="50%" pos="absolute" bottom="4%" left="1%">
                {notifications.map((n) => buildLeftNotifications(n, () => removeNotification(n.id)))}
            </Stack>
            <Stack w="50%" pos="absolute" bottom="4%" right="1%">
                {notifications.map((n) => buildRightNotifications(n, () => removeNotification(n.id)))}
            </Stack>
        </>
    );
}
